import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Quote, BadgeCheck, PenLine } from "lucide-react";
import { api } from "@/lib/api";
import { Stars } from "@/components/Stars";

export const useReviews = () =>
  useQuery({ queryKey: ["reviews"], queryFn: () => api.get("/reviews").then((r) => r.data) });

export const RatingSummary = ({ average, count }) => (
  <div className="flex items-center gap-3" data-testid="rating-summary">
    <span className="font-display text-4xl text-white" data-testid="rating-average">{Number(average).toFixed(1)}</span>
    <div>
      <Stars value={average} size={18} testid="rating-summary-stars" />
      <p className="text-white/50 text-xs mt-1" data-testid="rating-count">{count} verified client review{count === 1 ? "" : "s"}</p>
    </div>
  </div>
);

const ReviewCard = ({ r, i }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    transition={{ duration: 0.5, delay: (i % 3) * 0.08 }} data-testid={`review-card-${i}`}
    className="relative bg-[#111] border border-white/10 rounded-2xl p-7 hover:border-[#D4AF37]/40 transition-colors flex flex-col">
    <Quote className="absolute top-6 right-6 text-[#D4AF37]/15" size={40} />
    <Stars value={r.rating} />
    <p className="text-white/75 text-sm leading-relaxed mt-4 flex-1">“{r.text}”</p>
    <div className="mt-6 pt-4 border-t border-white/10">
      <p className="text-white font-medium text-sm flex items-center gap-1.5">
        {r.name}{r.verified && <BadgeCheck size={15} className="text-[#D4AF37]" />}
      </p>
      {r.service_title && <p className="font-mono-label uppercase text-[10px] tracking-[0.2em] text-[#D4AF37] mt-1">{r.service_title}</p>}
    </div>
  </motion.div>
);

export const WriteReviewLink = () => (
  <Link to="/review" data-testid="write-review-btn"
    className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 border border-white/20 text-white text-sm font-mono-label uppercase tracking-wide hover:border-[#D4AF37] transition-colors">
    <PenLine size={15} className="text-[#D4AF37]" /> Write a Review
  </Link>
);

export const Testimonials = () => {
  const { data } = useReviews();
  const items = data?.items || [];
  return (
    <section className="max-w-6xl mx-auto px-5 py-16" data-testid="testimonials-section">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <p className="font-mono-label uppercase tracking-[0.3em] text-xs text-[#D4AF37]">Client Love</p>
          <h2 className="font-display text-4xl md:text-5xl text-white mt-3">What Clients Say</h2>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          {items.length > 0 && <RatingSummary average={data.average} count={data.count} />}
          <WriteReviewLink />
        </div>
      </div>
      {items.length > 0 ? (
        <div className="grid md:grid-cols-3 gap-5">
          {items.slice(0, 6).map((r, i) => <ReviewCard key={r.id} r={r} i={i} />)}
        </div>
      ) : (
        <p className="text-white/40 text-sm" data-testid="reviews-empty">Be the first to share your XMD experience.</p>
      )}
    </section>
  );
};

export const ReviewsStrip = () => {
  const { data } = useReviews();
  const items = data?.items || [];
  if (!items.length) return null;
  return (
    <section className="mt-20" data-testid="reviews-strip">
      <div className="flex flex-wrap items-center justify-between gap-6 mb-6">
        <RatingSummary average={data.average} count={data.count} />
        <WriteReviewLink />
      </div>
      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 snap-x">
        {items.slice(0, 10).map((r, i) => (
          <div key={r.id} className="snap-start shrink-0 w-[280px] md:w-[340px] bg-[#111] border border-white/10 rounded-2xl p-5" data-testid={`strip-review-${i}`}>
            <Stars value={r.rating} size={14} />
            <p className="text-white/70 text-sm mt-3 line-clamp-4">“{r.text}”</p>
            <p className="text-white text-xs font-medium mt-4 flex items-center gap-1.5">
              {r.name}{r.verified && <BadgeCheck size={13} className="text-[#D4AF37]" />}
              {r.service_title && <span className="text-white/40 font-normal">· {r.service_title}</span>}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};
